import { apiClient, API_PRX } from '@indipia/shared';
import type { ApiResponse } from '@indipia/shared-types';

// ── 타입 ──────────────────────────────────────────────────────────────────────

export type StaffStatus = 'PENDING' | 'ACTIVE' | 'REJECTED' | 'RELEASED';
export type EmployeeAction = 'approve' | 'reject' | 'release';

export interface EmployeeItem {
  roleId: number;
  memberId: number;
  loginId: string;
  memberName: string;
  createdAt: string;
  joinedDtm: string | null;
  staffStatus: StaffStatus;
}

// ── GET /member/employees (마스터 전용 — 전체 직원 목록) ─────────────────────

export async function fetchEmployeeList(): Promise<EmployeeItem[]> {
  const res = await apiClient.get<ApiResponse<EmployeeItem[]>>(
    `${API_PRX.MEMBER}/employees`,
  );
  return (res.data.data ?? []) as EmployeeItem[];
}

// ── PATCH /member/employees/{roleId}/status (마스터 전용) ────────────────────

export async function updateEmployeeStatus(
  roleId: number,
  action: EmployeeAction,
): Promise<{ roleId: number; staffStatus: StaffStatus }> {
  const res = await apiClient.patch<ApiResponse<{ roleId: number; staffStatus: StaffStatus }>>(
    `${API_PRX.MEMBER}/employees/${roleId}/status`,
    { action },
  );
  return res.data.data;
}

// ── GET /member/employees/join-status ───────────────────────────────────────

export interface JoinStatus {
  staffStatus: 'PENDING' | 'ACTIVE' | 'REJECTED' | 'RELEASED' | null;
  companyName: string | null;
  invitedDtm: string | null;
}

export async function fetchJoinStatus(): Promise<JoinStatus> {
  const res = await apiClient.get<ApiResponse<JoinStatus>>(
    `${API_PRX.MEMBER}/employees/join-status`,
  );
  return res.data.data;
}

// ── GET /member/biz/search ──────────────────────────────────────────────────

export interface BizSearchItem {
  compId: number;
  compNo: string;
  compName: string;
  compRepName: string;
  hasMaster: boolean;
}

export async function searchBiz(keyword: string): Promise<BizSearchItem[]> {
  const res = await apiClient.get<ApiResponse<BizSearchItem[]>>(
    `${API_PRX.MEMBER}/biz/search`,
    { params: { keyword, hasMaster: 'Y' } },
  );
  return res.data.data ?? [];
}

// ── POST /member/employees/request-affiliation ──────────────────────────────

export interface AffiliationResult {
  roleId: number;
  staffStatus: string;
}

export async function requestAffiliation(compNo: string): Promise<AffiliationResult> {
  const res = await apiClient.post<ApiResponse<AffiliationResult>>(
    `${API_PRX.MEMBER}/employees/request-affiliation`,
    { compNo },
  );
  return res.data.data;
}

// ── PATCH /member/employees/request-affiliation/{roleId} ────────────────────

export async function cancelAffiliation(roleId: number): Promise<void> {
  await apiClient.patch(
    `${API_PRX.MEMBER}/employees/request-affiliation/${roleId}`,
  );
}
